import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { listFaaMakes, listFaaModelsOfMake, stateExperienceRowModel, stateExperienceHours } from '@/services/deals.service.js';

// THE EXPERIENCE SELECTION (David 2026-10-29). Two kinds, each from a refusal
// (utils/carrier-presubmit), the selection riding in as router state:
//   model     no line is in the FAA model family of an insured aircraft: per
//             line, pick the FAA model it means from the registry lists (make,
//             then model), then say whether it counts as the aircraft's make
//             and model. The stamp is the line's FAA model only; a yes writes
//             the family entry through its gated door.
//   disagree  one FAA model stated twice with two numbers: pick which is his.
// NO TYPING ANYWHERE: every answer is a pick from a list the server holds.

const hrs = (v) => (v == null || v === '' ? 'blank' : String(v));

function AircraftLine({ ac }) {
  return (
    <div className="text-[12.5px] text-text-dim">
      Insured aircraft {ac.tail}: FAA model {ac.faa_model_name || 'not in the registry'}{ac.faa_model_code ? ` (${ac.faa_model_code})` : ''}
    </div>
  );
}

export function ModelPick({ defaultMake, onPick }) {
  const [makes, setMakes] = useState(null);
  const [make, setMake] = useState(defaultMake || '');
  const [models, setModels] = useState(null);
  const [code, setCode] = useState('');
  useEffect(() => {
    let live = true;
    listFaaMakes().then((r) => { if (live) setMakes(r?.makes || []); }).catch((e) => { if (live) { setMakes([]); toast.error(`Could not list FAA makes: ${e.message}`); } });
    return () => { live = false; };
  }, []);
  useEffect(() => {
    if (!make) { setModels(null); return undefined; }
    let live = true;
    setModels(null); setCode(''); onPick(null);
    listFaaModelsOfMake(make).then((r) => { if (live) setModels(r?.models || []); }).catch((e) => { if (live) { setModels([]); toast.error(`Could not list ${make} models: ${e.message}`); } });
    return () => { live = false; };
  }, [make, onPick]);
  const cls = 'w-full rounded-[5px] border border-border bg-surface px-2 py-1.5 text-[13px] text-text';
  return (
    <div className="grid grid-cols-2 gap-2">
      <select className={cls} value={make} onChange={(e) => setMake(e.target.value)} disabled={!makes}>
        <option value="">{makes ? 'Make' : 'Loading makes'}</option>
        {(makes || []).map((m) => <option key={m.make} value={m.make}>{m.make}</option>)}
      </select>
      <select className={cls} value={code} disabled={!models || !models.length}
        onChange={(e) => { setCode(e.target.value); onPick((models || []).find((m) => m.faa_model_code === e.target.value) || null); }}>
        <option value="">{!make ? 'Pick a make first' : models ? 'FAA model' : 'Loading models'}</option>
        {(models || []).map((m) => <option key={m.faa_model_code} value={m.faa_model_code}>{m.model} ({m.faa_model_code}, {m.registered} registered)</option>)}
      </select>
    </div>
  );
}

// The line's FAA model only. "Counts as the aircraft's make and model" left 2026-10-31: the FAA-model family store is
// frozen (David); families are AMIN models, placed in Admin > Aircraft models.
function ModelRow({ row, selection, onDone }) {
  const [model, setModel] = useState(null);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await stateExperienceRowModel({ phf_id: selection.phf_id, row_key: row.row_key, faa_model_code: model.faa_model_code });
      toast.success(`${row.make_model}: FAA ${res.stamp.make} ${res.stamp.model}`);
      onDone(row.row_key);
    } catch (e) {
      toast.error(`Not saved: ${e.message}`);
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2 rounded-[6px] border border-border p-3">
      <div className="text-[13px] text-text">
        As stated: <span className="font-medium">{row.make_model}</span>, {hrs(row.total_hours)} total, {hrs(row.last_12_months)} last 12 months
      </div>
      {selection.aircraft.map((a) => <AircraftLine key={a.tail} ac={a} />)}
      <div className="text-[12px] text-text-dim">Which FAA model does this line mean?</div>
      <ModelPick defaultMake={selection.aircraft[0]?.registry_make || ''} onPick={setModel} />
      <button type="button" disabled={!model || saving} onClick={save}
        className="inline-flex items-center rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50">
        {saving ? 'Saving' : model ? `Save: this line is FAA ${model.make || ''} ${model.model || ''}`.replace(/\s+/g, ' ').trim() : 'Pick the FAA model'}
      </button>
    </div>
  );
}

function DisagreeRow({ selection, onDone }) {
  const [value, setValue] = useState(null);
  const [saving, setSaving] = useState(false);
  const word = selection.field === 'total' ? 'total' : 'last 12 months';
  const save = async () => {
    setSaving(true);
    try {
      await stateExperienceHours({ phf_id: selection.phf_id, faa_model_code: selection.faa_model_code, field: selection.field, value });
      toast.success(`${selection.pilot}: ${word} is ${value} hours`);
      onDone('disagree');
    } catch (e) { toast.error(`Not saved: ${e.message}`); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-2 rounded-[6px] border border-border p-3 text-[13px] text-text">
      <div>These lines are one FAA model ({selection.faa_model_code}) and state two {word} numbers:</div>
      <ul className="ml-4 list-disc">{(selection.rows || []).map((r) => <li key={r.row_key}>{r.make_model}: {hrs(r.value)}</li>)}</ul>
      {selection.aircraft.map((a) => <AircraftLine key={a.tail} ac={a} />)}
      <div className="text-[12px] text-text-dim">Which is {selection.pilot}'s {word}?</div>
      {(selection.values || []).map((v) => (
        <label key={v} className="mr-4"><input type="radio" checked={value === v} onChange={() => setValue(v)} /> {v}</label>
      ))}
      <div>
        <button type="button" disabled={value === null || saving} onClick={save}
          className="inline-flex items-center rounded-[5px] border border-accent bg-accent px-3 py-1.5 text-[12.5px] text-white hover:brightness-110 disabled:opacity-50">
          {saving ? 'Saving' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export function ExperienceModelDialog({ selection, open, onOpenChange }) {
  const [done, setDone] = useState([]);
  useEffect(() => { if (open) setDone([]); }, [open, selection?.phf_id]);
  if (!selection) return null;
  const isDisagree = selection.kind === 'disagree';
  const rows = isDisagree ? [] : (selection.rows || []).filter((r) => r.row_key);
  const left = isDisagree ? (done.length ? [] : ['disagree']) : rows.filter((r) => !done.includes(r.row_key));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{selection.pilot}: {isDisagree ? 'which number is his?' : 'which FAA model is each line?'}</DialogTitle>
          <DialogDescription>
            {isDisagree
              ? 'The pilot stated one FAA model twice with two numbers. Your answer is kept as the stated value.'
              : 'No line is in the same FAA model family as the insured aircraft. Time in another make and model never counts.'}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {isDisagree && left.length ? <DisagreeRow selection={selection} onDone={(k) => setDone((d) => [...d, k])} /> : null}
          {!isDisagree ? left.map((r) => <ModelRow key={r.row_key} row={r} selection={selection} onDone={(k) => setDone((d) => [...d, k])} />) : null}
          {!left.length ? <div className="text-[13px] text-text">Answered. Send to the carrier again.</div> : null}
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)}
            className="inline-flex items-center rounded-[5px] border border-border px-3 py-1.5 text-[12.5px] text-text hover:bg-surface-strong">
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
